import type { JSX, ParentComponent } from "solid-js";
import { Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { CoverButton } from "./CoverButton";
import { CoverLink } from "./CoverLink";
import "./Card.css";

export type CardHeaderProps = {
	class?: string;
	classList?: Record<string, boolean>;
};

export type Props = {
	as?: "div" | "a" | "button";
	href?: string;
	target?: HTMLAnchorElement["target"];
	rel?: HTMLAnchorElement["rel"];
	class?: string;
	classList?: Record<string, boolean>;
	borderRadius?: "standard" | "large";
	theme?:
		| "white"
		| "grey"
		| "dark"
		| "orange"
		| "danger"
		| "mint-grad"
		| "primary"
		| "blue";
	style?: JSX.CSSProperties;
	onClick?: JSX.EventHandlerUnion<HTMLElement, MouseEvent>;
	onDragEnter?: JSX.EventHandlerUnion<HTMLElement, DragEvent>;
	onDragLeave?: JSX.EventHandlerUnion<HTMLElement, DragEvent>;
	onDragOver?: JSX.EventHandlerUnion<HTMLElement, DragEvent>;
	onDrop?: JSX.EventHandlerUnion<HTMLElement, DragEvent>;
	"aria-label"?: string;
	type?: "button" | "submit" | "reset";
	disabled?: boolean;
	noPadding?: boolean;
	noBorder?: boolean;
	/** Activates the whole card, via a cover button. */
	onActivate?: (event: MouseEvent) => void;
	/** Navigates from the whole card, via a cover link. */
	coverHref?: string;
	/**
	 * Names the card for a screen reader. A cover has no content of its own,
	 * so give this whenever `onActivate` or `coverHref` is set.
	 */
	activateLabel?: string;
};

/**
 * Container for content with optional link, themes, and border radius variants.
 *
 * A card that is itself clickable can say so with `as`/`href`/`onClick`, but
 * only while it holds nothing clickable of its own. Where it does, use
 * `onActivate` or `coverHref` instead: the card stays a plain `div` and gets a
 * transparent cover, so its own controls sit beside the activating element
 * rather than inside it. Lift each cluster of them above the cover with
 * `gvtui-card__actions`, or the cover will take their clicks. A covered card
 * gives its `onClick` to the cover, since that is now the card's clickable
 * surface, and the controls should not have to fend it off.
 *
 * [Design System →](http://localhost:5174/component/Card)
 */
export const Card: ParentComponent<Props> = (props) => {
	const covered = () =>
		props.onActivate !== undefined || props.coverHref !== undefined;
	const klassName = () =>
		`gvtui-card ${props.class ?? ""} ${props.borderRadius ? `gvtui-card--border-radius-${props.borderRadius}` : ""} ${props.theme ? `gvtui-card--theme-${props.theme}` : ""}`;
	const component = () => props.as ?? (props.href ? "a" : "div");
	return (
		<Dynamic
			component={component()}
			class={klassName()}
			classList={{
				...props.classList,
				"gvtui-card-no-padding": props.noPadding,
				"gvtui-card-no-border": props.noBorder,
				"gvtui-card--covered": covered(),
			}}
			style={props.style}
			onClick={covered() ? undefined : props.onClick}
			onDragEnter={props.onDragEnter}
			onDragLeave={props.onDragLeave}
			onDragOver={props.onDragOver}
			onDrop={props.onDrop}
			target={props.target}
			aria-label={props["aria-label"]}
			href={component() === "a" ? props.href : undefined}
			type={component() === "button" ? (props.type ?? "button") : undefined}
			rel={component() === "a" ? props.rel : undefined}
			disabled={component() === "button" ? props.disabled : undefined}
		>
			{/* First, so that anything positioned after it hit-tests above it. */}
			<Show when={props.coverHref}>
				{(href) => (
					<CoverLink
						label={props.activateLabel ?? ""}
						href={href()}
						target={props.target}
						rel={props.rel}
						onClick={props.onClick}
					/>
				)}
			</Show>
			<Show when={props.onActivate}>
				{(onActivate) => (
					<CoverButton
						label={props.activateLabel ?? ""}
						onActivate={(event) => onActivate()(event)}
						disabled={props.disabled}
					/>
				)}
			</Show>
			{props.children}
		</Dynamic>
	);
};

/**
 * Flush label row for a `noPadding` card: padding plus a bottom rule.
 */
export const CardHeader: ParentComponent<CardHeaderProps> = (props) => (
	<div
		class={`gvtui-card-header ${props.class ?? ""}`}
		classList={props.classList}
	>
		{props.children}
	</div>
);

/** One of a list of rows in a `noPadding` card, ruled off from the row above. */
export const CardRow: ParentComponent<CardHeaderProps> = (props) => (
	<div
		class={`gvtui-card-row ${props.class ?? ""}`}
		classList={props.classList}
	>
		{props.children}
	</div>
);

/** A closing note under a `noPadding` card's rows. */
export const CardFooter: ParentComponent<CardHeaderProps> = (props) => (
	<div
		class={`gvtui-card-footer ${props.class ?? ""}`}
		classList={props.classList}
	>
		{props.children}
	</div>
);
